import { UseFormSetValue, UseFormWatch } from "react-hook-form";
import { motion } from "motion/react";
import { LuMapPin } from "react-icons/lu";
import { ActivityFormValues } from "../../zod/activitySchema";
import { XMarkIcon } from "@heroicons/react/24/outline";
import LocationSearchPicker from "../../../../../../../../../../components/LocationSearchPicker";

interface LocationSectionProps {
  displayLocation: boolean;
  setDisplayLocation: (display: boolean) => void;
  watch: UseFormWatch<ActivityFormValues>;
  setValue: UseFormSetValue<ActivityFormValues>;
}

export default function LocationSection({
  displayLocation,
  setDisplayLocation,
  watch,
  setValue,
}: LocationSectionProps) {
  const locationLabel = watch("locationLabel");
  const locationAddress = watch("locationAddress");

  const locationInputs = () => {
    if (!locationLabel && !locationAddress) {
      return (
        <div className="min-w-md">
          <LocationSearchPicker
            onLocationSelect={(location) => {
              setValue("locationLabel", location.name);
              setValue("locationAddress", location.address);
              setValue("locationZipCode", location.zipcode || "");
              setValue("locationCity", location.city || "");
              setValue("locationCountryCode", location.countryCode || "");
              setValue("locationLatitude", location.latitude);
              setValue("locationLongitude", location.longitude);
              setValue("locationPhotoUrls", location.photoUrls || []);
            }}
          />
        </div>
      );
    }

    return (
      <div>
        <div className="text-sm font-medium text-secondary">Nom du lieu</div>
        <div className="text-sm text-primary">{watch("locationLabel")}</div>
        <div className="text-sm font-medium text-secondary">Adresse</div>
        <div className="text-sm text-primary">{watch("locationAddress")}</div>
        <div className="text-sm font-medium text-secondary">Code postal</div>
        <div className="text-sm text-primary">{watch("locationZipCode")}</div>
        <div className="text-sm font-medium text-secondary">Ville</div>
        <div className="text-sm text-primary">{watch("locationCity")}</div>
      </div>
    );
  };

  if (displayLocation) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.3 }}
        className="border border-tertiary/20 rounded-md p-4"
      >
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="text-lg font-medium">Lieu</div>
              <XMarkIcon
                className="text-muted h-4 w-4 cursor-pointer hover:opacity-80"
                onClick={() => setDisplayLocation(false)}
              />
            </div>
            {(locationLabel || locationAddress) && (
              <button
                className="text-accent hover:text-accent/80 transition-all duration-300 cursor-pointer"
                onClick={() => {
                  setValue("locationAddress", "");
                  setValue("locationCity", "");
                  setValue("locationLabel", "");
                  setValue("locationZipCode", "");
                  setValue("locationCountryCode", "");
                  setValue("locationLatitude", undefined);
                  setValue("locationLongitude", undefined);
                  setValue("locationPhotoUrls", []);
                }}
              >
                <div className="flex items-center gap-2 text-sm">
                  Rechercher avec Google Maps
                </div>
              </button>
            )}
          </div>

          {locationInputs()}
        </div>
      </motion.div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 cursor-pointer text-secondary hover:text-primary/80 transition-all duration-300"
      onClick={() => setDisplayLocation(true)}
    >
      <LuMapPin className="w-4 h-4" />
      Ajouter un <span className="underline">lieu</span>
    </div>
  );
}
